import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Download, RotateCcw, Filter, ArrowUpDown, CalendarDays } from "lucide-react";
import { useData } from "../lib/data-context";
import { api, fmtDec, fmtInt, fmtMWh } from "../lib/api";
import Semaforo from "../components/Semaforo";
import ViabilidadLetra from "../components/ViabilidadLetra";

const VIABS = ["Alta", "Condicionada", "A revisar"];
const SERVICIOS = ["Urbano", "Interurbano", "Nocturno", "Especial", "Mixto"];

export default function Tabla() {
  const { lineas: lineasBase, lotes, loading } = useData();
  const nav = useNavigate();
  const [lote, setLote] = useState("");
  const [viab, setViab] = useState("");
  const [tipo, setTipo] = useState("");
  const [tamano, setTamano] = useState("");
  const [servicio, setServicio] = useState("");
  const [tipoDia, setTipoDia] = useState("");
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("consumo_kwh_km_base");
  const [sortDir, setSortDir] = useState("desc");

  // Catálogo de tipos de día del Anexo V (literales reales)
  const [tiposDiaOpts, setTiposDiaOpts] = useState([]);
  useEffect(() => {
    api.get("/tipos-dia").then((r) => setTiposDiaOpts(r.data || [])).catch(() => setTiposDiaOpts([]));
  }, []);

  // Líneas con métricas recalculadas para el tipo de día seleccionado.
  // Si no hay filtro, se usan las líneas base ya cargadas por el data-context.
  const [lineasFiltradasPorTipoDia, setLineasFiltradasPorTipoDia] = useState(null);
  const [loadingTipoDia, setLoadingTipoDia] = useState(false);
  useEffect(() => {
    let alive = true;
    if (!tipoDia) {
      setLineasFiltradasPorTipoDia(null);
      return undefined;
    }
    setLoadingTipoDia(true);
    api.get("/lineas", { params: { tipo_dia: tipoDia } })
      .then((r) => { if (alive) setLineasFiltradasPorTipoDia(r.data); })
      .catch(() => { if (alive) setLineasFiltradasPorTipoDia([]); })
      .finally(() => { if (alive) setLoadingTipoDia(false); });
    return () => { alive = false; };
  }, [tipoDia]);

  const lineas = lineasFiltradasPorTipoDia || lineasBase;

  const tipologias = useMemo(
    () => [...new Set((lineas || []).map((l) => l.clase_vehiculo))].sort(),
    [lineas]
  );
  const tamanos = useMemo(
    () => [...new Set((lineas || []).map((l) => l.tamano_vehiculo))].sort(),
    [lineas]
  );

  const rows = useMemo(() => {
    if (!lineas) return [];
    let r = lineas.filter((l) => {
      if (lote && l.lote !== lote) return false;
      if (viab && l.viabilidad !== viab) return false;
      if (tipo && l.clase_vehiculo !== tipo) return false;
      if (tamano && l.tamano_vehiculo !== tamano) return false;
      if (servicio && l.tipo_servicio !== servicio) return false;
      // Si el filtro tipo_dia está activo, ocultamos las líneas que no operan ese día.
      if (tipoDia && (!l.veh_km_anuales || l.veh_km_anuales === 0)) return false;
      if (q) {
        const s = q.toLowerCase();
        if (
          !l.numero_linea.toLowerCase().includes(s) &&
          !l.denominacion.toLowerCase().includes(s) &&
          !l.codigo_linea.toLowerCase().includes(s)
        )
          return false;
      }
      return true;
    });
    // Cuando hay filtro de tipo_dia, ordenamos por consumo anual desc por defecto
    // para que las líneas con mayor servicio en ese día aparezcan primero.
    const effSortBy = tipoDia && sortBy === "consumo_kwh_km_base" ? "consumo_anual_estimado_kwh" : sortBy;
    r.sort((a, b) => {
      const va = a[effSortBy];
      const vb = b[effSortBy];
      if (typeof va === "number") return sortDir === "asc" ? va - vb : vb - va;
      return sortDir === "asc"
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
    return r;
  }, [lineas, lote, viab, tipo, tamano, servicio, tipoDia, q, sortBy, sortDir]);

  const totalLineasFiltro = useMemo(
    () => (lineas || []).filter((l) => {
      if (lote && l.lote !== lote) return false;
      return true;
    }).length,
    [lineas, lote]
  );
  const ocultas = tipoDia ? totalLineasFiltro - rows.length : 0;

  const setSort = (col) => {
    if (col === sortBy) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortBy(col);
      setSortDir("desc");
    }
  };

  const reset = () => {
    setLote(""); setViab(""); setTipo(""); setTamano(""); setServicio(""); setQ(""); setTipoDia("");
  };

  const exportCSV = () => {
    const headers = [
      "lote","codigo_linea","numero_linea","denominacion","tipo_servicio","tipologia_vehiculo",
      "tamano_vehiculo","etiqueta_ambiental","longitud_km","expediciones_anuales","veh_km_anuales",
      "consumo_kwh_km_base","consumo_kwh_km_conservador","consumo_por_expedicion_kwh",
      "consumo_diario_estimado_kwh","consumo_anual_estimado_kwh",
      "viabilidad","viabilidad_letra","viabilidad_score","observaciones",
    ];
    // Si hay filtro de tipo de día, añadimos una columna explícita y un sufijo al nombre del fichero
    const allHeaders = tipoDia ? [...headers, "tipo_dia_filtro"] : headers;
    const csv = [
      allHeaders.join(";"),
      ...rows.map((l) => allHeaders.map((h) => {
        const v = h === "tipo_dia_filtro" ? tipoDia : l[h];
        return `"${String(v ?? "").replace(/"/g, '""')}"`;
      }).join(";")),
    ].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const tipoSlug = tipoDia ? `_${tipoDia.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}` : "";
    a.download = `voltia_lineas${tipoSlug}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="text-gray-500">Cargando…</div>;

  return (
    <div className="space-y-5" data-testid="tabla-page">
      <div>
        <div className="text-xs font-semibold tracking-widest text-gray-500 uppercase">Tabla principal de resultados</div>
        <h1 className="section-title mt-1">Líneas analizadas ({fmtInt(rows.length)})</h1>
        <p className="section-sub">Filtra, ordena y exporta los resultados del estudio. Haz clic en una línea para ver su ficha.</p>
      </div>

      {/* Filters */}
      <div className="crtm-card p-5">
        <div className="flex items-center gap-2 mb-4 text-gray-700">
          <Filter size={16} />
          <span className="text-sm font-semibold">Filtros</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <div className="lg:col-span-2">
            <label className="kpi-label">Búsqueda libre</label>
            <div className="relative mt-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Línea, denominación, código…"
                data-testid="tabla-search"
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-gray-900"
              />
            </div>
          </div>
          <SelectField label="Lote" value={lote} onChange={setLote} options={lotes} testId="filter-lote" />
          <SelectField label="Servicio" value={servicio} onChange={setServicio} options={SERVICIOS} testId="filter-servicio" />
          <SelectField label="Tipología" value={tipo} onChange={setTipo} options={tipologias} testId="filter-tipologia" />
          <SelectField label="Tamaño" value={tamano} onChange={setTamano} options={tamanos} testId="filter-tamano" />
          <SelectField label="Viabilidad" value={viab} onChange={setViab} options={VIABS} testId="filter-viabilidad" />
          <div className="md:col-span-2 lg:col-span-3">
            <label className="kpi-label flex items-center gap-1">
              <CalendarDays size={12} /> Tipo de día (Anexo V)
            </label>
            <select
              value={tipoDia}
              onChange={(e) => setTipoDia(e.target.value)}
              data-testid="filter-tipo-dia"
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-gray-900"
            >
              <option value="">Todos los días (consumo anual total)</option>
              {tiposDiaOpts.map((t) => (
                <option key={t.tipo_dia} value={t.tipo_dia}>
                  {t.tipo_dia} · {t.categoria} · {fmtInt(t.n_expediciones)} expediciones/año ({t.n_lineas} líneas)
                </option>
              ))}
            </select>
          </div>
        </div>
        {tipoDia && (
          <div
            data-testid="tipo-dia-banner"
            className="mt-3 px-3 py-2 rounded-md text-xs flex items-center gap-2"
            style={{ background: "#14532D11", color: "#14532D", border: "1px solid #14532D33" }}
          >
            <CalendarDays size={14} />
            Mostrando consumo y veh-km <strong className="font-semibold">solo</strong> para el tipo de día
            <strong className="font-semibold">{` ${tipoDia} `}</strong>
            ({categoria(tipoDia, tiposDiaOpts)}).
            {ocultas > 0 && (
              <span className="opacity-80"> {fmtInt(ocultas)} líneas ocultas (no operan ese día).</span>
            )}
            {loadingTipoDia && <span className="opacity-60 ml-2">recalculando…</span>}
          </div>
        )}
        <div className="flex items-center justify-between mt-4">
          <div className="text-xs text-gray-500">{fmtInt(rows.length)} resultados</div>
          <div className="flex items-center gap-2">
            <button className="btn-secondary" onClick={reset} data-testid="btn-reset-filtros">
              <RotateCcw size={14} /> Restablecer filtros
            </button>
            <button className="btn-primary" onClick={exportCSV} data-testid="btn-export-csv">
              <Download size={14} /> Exportar CSV
            </button>
          </div>
        </div>
      </div>

      <div className="crtm-card overflow-x-auto">
        <table className="crtm-table">
          <thead>
            <tr>
              <Th onClick={() => setSort("lote")}>Lote</Th>
              <Th onClick={() => setSort("numero_linea")}>Línea</Th>
              <th>Denominación</th>
              <th>Servicio</th>
              <th>Tipología</th>
              <th>Etiqueta</th>
              <Th onClick={() => setSort("longitud_km")}>Long. (km)</Th>
              <Th onClick={() => setSort("veh_km_anuales")}>Veh-km</Th>
              <Th onClick={() => setSort("consumo_kwh_km_base")}>kWh/km base</Th>
              <Th onClick={() => setSort("consumo_kwh_km_conservador")}>kWh/km cons.</Th>
              <Th onClick={() => setSort("consumo_anual_estimado_kwh")}>Consumo anual</Th>
              {tipoDia && (
                <Th onClick={() => setSort("consumo_diario_estimado_kwh")}>
                  Consumo/día tipo
                </Th>
              )}
              <Th onClick={() => setSort("ascenso_por_km")}>Desnivel (m/km)</Th>
              <Th onClick={() => setSort("viabilidad_score")}>Score</Th>
              <th>Viabilidad</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((l) => (
              <tr
                key={l.codigo_linea}
                className="clickable"
                onClick={() => nav(`/linea/${encodeURIComponent(l.codigo_linea)}`)}
                data-testid={`row-${l.codigo_linea}`}
              >
                <td><span className="pill neutral">{l.lote}</span></td>
                <td className="font-semibold font-mono">{l.numero_linea}</td>
                <td className="max-w-[260px] truncate">{l.denominacion}</td>
                <td className="text-xs">{l.tipo_servicio}</td>
                <td className="text-xs">{l.tipologia_vehiculo}</td>
                <td className="text-xs">{l.etiqueta_ambiental}</td>
                <td className="font-mono">{fmtDec(l.longitud_km, 2)}</td>
                <td className="font-mono">{fmtInt(l.veh_km_anuales)}</td>
                <td className="font-mono font-semibold">{fmtDec(l.consumo_kwh_km_base, 2)}</td>
                <td className="font-mono">{fmtDec(l.consumo_kwh_km_conservador, 2)}</td>
                <td className="font-mono">{fmtMWh(l.consumo_anual_estimado_kwh)}</td>
                {tipoDia && (
                  <td
                    className="font-mono font-semibold"
                    title={`Consumo por día tipo "${tipoDia}". Estimado = consumo anual / días al año de ese tipo.`}
                    data-testid={`consumo-dia-${l.codigo_linea}`}
                  >
                    {l.consumo_diario_estimado_kwh > 0
                      ? `${fmtInt(l.consumo_diario_estimado_kwh)} kWh`
                      : <span className="text-gray-300">—</span>}
                  </td>
                )}
                <td className="font-mono">
                  {l.ascenso_por_km != null
                    ? <span title={`Asciende ${l.ascenso_total_m || 0} m, desciende ${l.descenso_total_m || 0} m`}>{fmtDec(l.ascenso_por_km, 1)}</span>
                    : <span className="text-gray-300">—</span>}
                </td>
                <td><ViabilidadLetra letra={l.viabilidad_letra} score={l.viabilidad_score} /></td>
                <td><Semaforo viabilidad={l.viabilidad} /></td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={tipoDia ? 15 : 14} className="text-center text-gray-400 py-8">No hay resultados con los filtros aplicados.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const SelectField = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="kpi-label">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:border-gray-900"
    >
      <option value="">Todos</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

// Devuelve la categoría asociada a un literal de tipo_dia consultando el catálogo del backend.
const categoria = (tipoDia, opts) => {
  const it = (opts || []).find((o) => o.tipo_dia === tipoDia);
  return it ? it.categoria : "—";
};

const Th = ({ children, onClick }) => (
  <th onClick={onClick} style={{ cursor: "pointer" }} className="select-none">
    <span className="inline-flex items-center gap-1">{children} <ArrowUpDown size={11} /></span>
  </th>
);
